import { Fragment, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Initials } from './ui';
import './Mentions.css';

export type Person = { id: string; name: string };

/** The "@word" being typed just before the cursor, if any. */
function activeQuery(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const m = /(^|\s)@([\p{L}'’-]{0,30})$/u.exec(before);
  return m ? { start: before.length - m[2].length - 1, query: m[2] } : null;
}

/** The people still tagged: those whose "@Name" is still in the text. */
export function mentionedIn(text: string, tagged: Person[]): string[] {
  return [...new Set(tagged.filter((p) => text.includes(`@${p.name}`)).map((p) => p.id))];
}

/**
 * A textarea where typing "@" suggests colleagues; choosing one writes
 * "@Name" into the text and remembers who it is. Arrow keys and Enter pick,
 * Escape closes. Tagged people are notified by the server.
 */
export function MentionTextarea({
  value,
  onChange,
  people,
  onTag,
  placeholder,
  maxLength,
  id,
  ariaLabel,
  autoFocus,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  people: Person[];
  onTag: (p: Person) => void;
  placeholder?: string;
  maxLength?: number;
  id?: string;
  ariaLabel?: string;
  autoFocus?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [caret, setCaret] = useState(0);
  const [index, setIndex] = useState(0);
  const [closed, setClosed] = useState(false);
  const q = closed ? null : activeQuery(value, caret);
  const matches = useMemo(() => {
    if (!q) return [];
    const needle = q.query.toLowerCase();
    return people.filter((p) => p.name.toLowerCase().split(/\s+/).some((w) => w.startsWith(needle)) || p.name.toLowerCase().startsWith(needle)).slice(0, 6);
  }, [q, people]);

  const pick = (p: Person) => {
    if (!q) return;
    const next = `${value.slice(0, q.start)}@${p.name} ${value.slice(caret)}`;
    onChange(next);
    onTag(p);
    const pos = q.start + p.name.length + 2;
    requestAnimationFrame(() => {
      ref.current?.focus();
      ref.current?.setSelectionRange(pos, pos);
      setCaret(pos);
    });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!matches.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIndex((i) => (i + 1) % matches.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIndex((i) => (i - 1 + matches.length) % matches.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      pick(matches[Math.min(index, matches.length - 1)]);
    } else if (e.key === 'Escape') {
      setClosed(true);
    }
  };

  return (
    <div className="mention-box">
      <textarea
        ref={ref}
        id={id}
        className={className ?? 'textarea'}
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        aria-label={ariaLabel}
        autoFocus={autoFocus}
        onChange={(e) => {
          onChange(e.target.value);
          setCaret(e.target.selectionStart ?? e.target.value.length);
          setIndex(0);
          setClosed(false);
        }}
        onKeyDown={onKeyDown}
        onClick={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
        onKeyUp={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
        aria-autocomplete="list"
        aria-expanded={matches.length > 0}
      />
      {matches.length ? (
        <ul className="mention-list" role="listbox" aria-label="Suggestions to tag">
          {matches.map((p, i) => (
            <li key={p.id} role="option" aria-selected={i === index}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(p);
                }}
                onMouseEnter={() => setIndex(i)}
              >
                <Initials name={p.name} size={26} />
                <span>{p.name}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Post or comment text with "@Name" for tagged people shown as a highlighted mention. */
export function MentionText({ text, mentions }: { text: string; mentions: Person[] }): ReactNode {
  if (!mentions.length) return text;
  const names = [...mentions].sort((a, b) => b.name.length - a.name.length).map((m) => m.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const parts = text.split(new RegExp(`(@(?:${names.join('|')}))`, 'g'));
  return parts.map((part, i) =>
    part.startsWith('@') && mentions.some((m) => part === `@${m.name}`) ? (
      <span key={i} className="mention">
        {part}
      </span>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    ),
  );
}
